import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import Header from "../components/Header";

const emptyCareer = {
  title: "",
  department: "",
  location: "",
  type: "",
  description: "",
  enabled: true,
};

const defaultFields = [
  {
    label: "Full Name",
    field_key: "full_name",
    field_type: "text",
    placeholder: "Enter your full name",
    required: true,
    sort_order: 1,
    enabled: true,
  },
  {
    label: "Email Address",
    field_key: "email",
    field_type: "email",
    placeholder: "you@example.com",
    required: true,
    sort_order: 2,
    enabled: true,
  },
];

export default function CareersCreate() {
  const [career, setCareer] = useState(emptyCareer);
  const [fields, setFields] = useState(defaultFields);

  const [careers, setCareers] = useState([]);
  const [selectedCareerId, setSelectedCareerId] = useState(null);

  const [loadingList, setLoadingList] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const isEditing = Boolean(selectedCareerId);

  useEffect(() => {
    loadCareers();
  }, []);

  async function loadCareers() {
    setLoadingList(true);

    const { data, error } = await supabase
      .from("careers")
      .select("*")
      .order("created_at", { ascending: false });

    if (!error) setCareers(data || []);

    setLoadingList(false);
  }

  async function loadCareerForEdit(id) {
    setMessage(null);
    setSelectedCareerId(id);

    const { data: careerData, error: careerError } = await supabase
      .from("careers")
      .select("*")
      .eq("id", id)
      .single();

    if (careerError) {
      setMessage({ type: "error", text: careerError.message });
      return;
    }

    const { data: fieldData, error: fieldError } = await supabase
      .from("career_fields")
      .select("*")
      .eq("career_id", id)
      .order("sort_order", { ascending: true });

    if (fieldError) {
      setMessage({ type: "error", text: fieldError.message });
      return;
    }

    setCareer({
      title: careerData.title || "",
      department: careerData.department || "",
      location: careerData.location || "",
      type: careerData.type || "",
      description: careerData.description || "",
      enabled: Boolean(careerData.enabled),
    });

    setFields(fieldData?.length ? fieldData : []);
  }

  function resetForm() {
    setSelectedCareerId(null);
    setCareer(emptyCareer);
    setFields(defaultFields);
    setMessage(null);
  }

  function updateCareer(key, value) {
    setCareer((prev) => ({ ...prev, [key]: value }));
  }

  function updateField(index, key, value) {
    setFields((prev) =>
      prev.map((field, i) =>
        i === index ? { ...field, [key]: value } : field,
      ),
    );
  }

  function addField() {
    setFields((prev) => [
      ...prev,
      {
        label: "",
        field_key: "",
        field_type: "text",
        placeholder: "",
        required: false,
        sort_order: prev.length + 1,
        enabled: true,
      },
    ]);
  }

  function removeField(index) {
    setFields((prev) => prev.filter((_, i) => i !== index));
  }

  function makeFieldKey(label) {
    return label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
  }

  async function toggleCareerEnabled(careerItem) {
    const { error } = await supabase
      .from("careers")
      .update({ enabled: !careerItem.enabled })
      .eq("id", careerItem.id);

    if (error) {
      setMessage({ type: "error", text: error.message });
      return;
    }

    await loadCareers();

    if (selectedCareerId === careerItem.id) {
      setCareer((prev) => ({ ...prev, enabled: !careerItem.enabled }));
    }
  }

  async function saveCareer(e) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      let careerId = selectedCareerId;

      if (isEditing) {
        const { error } = await supabase
          .from("careers")
          .update(career)
          .eq("id", selectedCareerId);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("careers")
          .insert(career)
          .select()
          .single();

        if (error) throw error;

        careerId = data.id;
      }

      const cleanedFields = fields
        .filter((field) => field.label.trim())
        .map((field, index) => ({
          career_id: careerId,
          label: field.label,
          field_key: field.field_key || makeFieldKey(field.label),
          field_type: field.field_type,
          placeholder: field.placeholder,
          required: field.required,
          sort_order: index + 1,
          enabled: field.enabled,
        }));

      if (isEditing) {
        const { error: deleteError } = await supabase
          .from("career_fields")
          .delete()
          .eq("career_id", careerId);

        if (deleteError) throw deleteError;
      }

      if (cleanedFields.length > 0) {
        const { error: fieldsError } = await supabase
          .from("career_fields")
          .insert(cleanedFields);

        if (fieldsError) throw fieldsError;
      }

      setMessage({
        type: "success",
        text: isEditing
          ? "Career updated successfully."
          : "Career created successfully.",
      });

      await loadCareers();

      if (!isEditing) resetForm();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.message || "Failed to save career.",
      });
    }

    setSaving(false);
  }

  const inputClass =
    "w-full rounded-xl border border-[#ffb56f]/30 bg-black/50 px-4 py-3 font-bold text-white outline-none placeholder:text-white/40 focus:border-[#ffd978]";

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <Header />

      <section className="px-6 pt-36 pb-16 md:px-[7%]">
        <p className="font-black uppercase text-[#ffb56f]">Admin</p>

        <h1 className="outfall-heading text-[clamp(58px,9vw,120px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
          Careers Admin
        </h1>

        <div className="mt-10 grid gap-8 xl:grid-cols-[420px_1fr]">
          <aside className="rounded-[28px] border-4 border-[#ffd978] bg-black/60 p-6 shadow-[0_0_35px_rgba(255,143,107,0.25)]">
            <div className="flex items-center justify-between gap-4">
              <h2 className="outfall-heading text-4xl text-[#ffd978]">
                Vacancies
              </h2>

              <button
                type="button"
                onClick={resetForm}
                className="rounded-xl bg-[#ffd978] px-4 py-2 font-black uppercase text-black"
              >
                New
              </button>
            </div>

            <div className="mt-5 grid gap-3">
              {loadingList && (
                <p className="font-black uppercase text-white/60">
                  Loading vacancies...
                </p>
              )}

              {!loadingList && careers.length === 0 && (
                <p className="font-black uppercase text-white/60">
                  No vacancies listed.
                </p>
              )}

              {careers.map((item) => (
                <div
                  key={item.id}
                  className={`rounded-2xl border p-4 ${
                    selectedCareerId === item.id
                      ? "border-[#ffd978] bg-[#ffd978]/10"
                      : "border-[#ffb56f]/30 bg-black/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-black uppercase text-white">
                        {item.title}
                      </h3>

                      <p className="mt-1 text-sm font-bold text-white/55">
                        {item.department || "No department"} ·{" "}
                        {item.location || "No location"}
                      </p>

                      <span
                        className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-black uppercase ${
                          item.enabled
                            ? "bg-green-400 text-black"
                            : "bg-red-500 text-white"
                        }`}
                      >
                        {item.enabled ? "Visible" : "Disabled"}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => loadCareerForEdit(item.id)}
                      className="rounded-lg bg-white px-3 py-2 text-xs font-black uppercase text-black"
                    >
                      View / Edit
                    </button>

                    <button
                      type="button"
                      onClick={() => toggleCareerEnabled(item)}
                      className={`rounded-lg px-3 py-2 text-xs font-black uppercase ${
                        item.enabled
                          ? "bg-red-600 text-white"
                          : "bg-green-400 text-black"
                      }`}
                    >
                      {item.enabled ? "Disable" : "Enable"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <form
            onSubmit={saveCareer}
            className="rounded-[28px] border-4 border-[#ffd978] bg-black/60 p-6 shadow-[0_0_35px_rgba(255,143,107,0.35)] md:p-10"
          >
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <h2 className="outfall-heading text-5xl text-[#ffd978]">
                {isEditing ? "Edit Career" : "Create Career"}
              </h2>

              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-xl border border-white/20 px-4 py-2 font-black uppercase text-white"
                >
                  Cancel Edit
                </button>
              )}
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <input
                className={inputClass}
                placeholder="Job title"
                required
                value={career.title}
                onChange={(e) => updateCareer("title", e.target.value)}
              />

              <input
                className={inputClass}
                placeholder="Department"
                value={career.department}
                onChange={(e) => updateCareer("department", e.target.value)}
              />

              <input
                className={inputClass}
                placeholder="Location"
                value={career.location}
                onChange={(e) => updateCareer("location", e.target.value)}
              />

              <input
                className={inputClass}
                placeholder="Type, e.g. Volunteer / Paid / Remote"
                value={career.type}
                onChange={(e) => updateCareer("type", e.target.value)}
              />
            </div>

            <textarea
              className={`${inputClass} mt-5 min-h-[180px] resize-y`}
              placeholder="Job description"
              required
              value={career.description}
              onChange={(e) => updateCareer("description", e.target.value)}
            />

            <label className="mt-5 flex items-center gap-3 font-black uppercase text-[#ffb56f]">
              <input
                type="checkbox"
                checked={career.enabled}
                onChange={(e) => updateCareer("enabled", e.target.checked)}
              />
              Career visible
            </label>

            <div className="mt-10 flex items-center justify-between gap-4">
              <h2 className="outfall-heading text-5xl text-[#ffd978]">
                Application Fields
              </h2>

              <button
                type="button"
                onClick={addField}
                className="outfall-button"
              >
                Add Field
              </button>
            </div>

            <div className="mt-6 grid gap-5">
              {fields.map((field, index) => (
                <div
                  key={index}
                  className="rounded-2xl border border-[#ffb56f]/30 bg-black/40 p-5"
                >
                  <div className="grid gap-4 md:grid-cols-2">
                    <input
                      className={inputClass}
                      placeholder="Label"
                      value={field.label}
                      onChange={(e) =>
                        updateField(index, "label", e.target.value)
                      }
                    />

                    <input
                      className={inputClass}
                      placeholder="Field key"
                      value={field.field_key}
                      onChange={(e) =>
                        updateField(index, "field_key", e.target.value)
                      }
                    />

                    <select
                      className={inputClass}
                      value={field.field_type}
                      onChange={(e) =>
                        updateField(index, "field_type", e.target.value)
                      }
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="email">Email</option>
                      <option value="textarea">Textarea</option>
                      <option value="file">File</option>
                      <option value="date">Date</option>
                      <option value="tel">Phone</option>
                      <option value="url">URL</option>
                    </select>

                    <input
                      className={inputClass}
                      placeholder="Placeholder"
                      value={field.placeholder}
                      onChange={(e) =>
                        updateField(index, "placeholder", e.target.value)
                      }
                    />
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-5">
                    <label className="flex items-center gap-2 font-black uppercase text-white">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) =>
                          updateField(index, "required", e.target.checked)
                        }
                      />
                      Required
                    </label>

                    <label className="flex items-center gap-2 font-black uppercase text-white">
                      <input
                        type="checkbox"
                        checked={field.enabled}
                        onChange={(e) =>
                          updateField(index, "enabled", e.target.checked)
                        }
                      />
                      Enabled
                    </label>

                    <button
                      type="button"
                      onClick={() => removeField(index)}
                      className="rounded-lg bg-red-600 px-4 py-2 font-black uppercase text-white"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {message && (
              <div
                className={`mt-6 rounded-xl px-4 py-3 font-black ${
                  message.type === "success"
                    ? "bg-green-500 text-black"
                    : "bg-red-500 text-white"
                }`}
              >
                {message.text}
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              className="outfall-button mt-8"
            >
              {saving
                ? isEditing
                  ? "Saving..."
                  : "Creating..."
                : isEditing
                  ? "Save Changes"
                  : "Create Career"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
